import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const biometricEnabled = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const userRole = computed(() => user.value?.role)
  const userName = computed(() => user.value?.displayName || user.value?.email)
  const householdId = computed(() => user.value?.householdId)

  async function initialize() {
    isLoading.value = true
    try {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        user.value = JSON.parse(storedUser)
      }

      const currentUser = await authService.getCurrentUser()
      if (currentUser) {
        user.value = currentUser
        localStorage.setItem('user', JSON.stringify(currentUser))
      }
    } catch (e) {
      error.value = (e as Error).message
    } finally {
      isLoading.value = false
    }
  }

  async function login(email: string, password: string) {
    isLoading.value = true
    error.value = null
    try {
      user.value = await authService.login(email, password)
      localStorage.setItem('user', JSON.stringify(user.value))
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function register(email: string, password: string) {
    isLoading.value = true
    error.value = null
    try {
      user.value = await authService.register(email, password)
      localStorage.setItem('user', JSON.stringify(user.value))
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function socialLogin(provider: 'google' | 'facebook') {
    isLoading.value = true
    error.value = null
    try {
      user.value = await authService.socialLogin(provider)
      localStorage.setItem('user', JSON.stringify(user.value))
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    isLoading.value = true
    try {
      await authService.logout()
      user.value = null
      localStorage.removeItem('user')
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function updateProfile(data: Partial<User>) {
    if (!user.value) return
    isLoading.value = true
    try {
      user.value = await authService.updateProfile(data)
      localStorage.setItem('user', JSON.stringify(user.value))
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function deleteAccount(password: string) {
    isLoading.value = true
    try {
      await authService.deleteAccount(password)
      user.value = null
      localStorage.clear()
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  return {
    user,
    isLoading,
    error,
    biometricEnabled,
    isAuthenticated,
    userRole,
    userName,
    householdId,
    initialize,
    login,
    register,
    socialLogin,
    logout,
    updateProfile,
    deleteAccount,
    clearError
  }
}, {
  persist: {
    paths: ['user', 'biometricEnabled']
  }
})
